// ─────────────────────────────────────────────────────────────────────────────
// Item Master — shared Item type
//
// Superset of the three previously-divergent Item shapes (the create modal's
// form state, ItemMasterListPage's list-row type, ItemMasterDetailPage's
// detail/edit type) plus the new Division/Group/Grade/Pack Size/Tax1&2 fields.
// One type, imported everywhere Item Master reads or writes a product.
// ─────────────────────────────────────────────────────────────────────────────

/** A MasterItem reference: a bare id, or populated with its name. */
export type MasterRef = string | { _id: string; name?: string } | null;

/** The id of a MasterRef, whether it arrived bare or populated. */
export function masterId(ref: MasterRef | undefined): string {
  if (!ref) return '';
  return typeof ref === 'object' ? String(ref._id || '') : String(ref);
}

/** The display name of a MasterRef, or '' when it is only an id. */
export function masterName(ref: MasterRef | undefined): string {
  return ref && typeof ref === 'object' ? (ref.name || '') : '';
}

export interface PriceHistoryEntry {
  changedAt: string;
  field: string;
  oldValueMinor: number;
  newValueMinor: number;
  changedBy: string;
}

export interface AlternateUnit {
  unitId: string;
  unitName: string;
  conversionFactor: number;
  purchaseUom: boolean;
  salesUom: boolean;
}

export interface Item {
  _id: string;
  name: string;
  sku: string;
  barcode?: string;
  hsnSac?: string;
  description?: string;
  itemType: string;

  // Basic Details — new master-data taxonomy. Server keeps grade/packSize
  // (flat strings) in sync with gradeId/packSizeId so older readers of those
  // flat fields keep working untouched.
  // The list endpoint POPULATES these with their MasterItem name so the table
  // can show it; every other endpoint returns the bare id. Both shapes are
  // declared, and `masterName()` narrows them at the point of display.
  divisionId?: MasterRef;
  /** The middle taxonomy level: Division -> Category -> Group. Not to be
   *  confused with `categoryId` below, which is the older CatalogCategory
   *  collection the original Catalog module reads. */
  categoryMasterId?: MasterRef;
  groupId?: MasterRef;
  gradeId?: MasterRef;
  packSizeId?: MasterRef;
  /** API/OEM service class - SM, SL, SN, CF-4, GL-5 ... The same 20W-40 ships
   *  as SL, SM and SN, so it is a separate axis from `grade` (the viscosity).
   *  `selector` is the server-maintained flat mirror of `selectorId`. */
  selectorId?: MasterRef;
  selector?: string;
  /** Unit packs per case (20 x 800 ML), the case size as the source catalogue
   *  states it ('16 LTR'), and the unit pack size parsed to a number (800). */
  qtyPerPack?: number;
  totalPackSize?: string;
  packQuantity?: number;
  unitId?: string | null;
  grade?: string;
  packSize?: string;
  images?: string[];
  image?: string;

  isPurchased: boolean;
  isSold: boolean;
  isProduced: boolean;
  isActive: boolean;

  // Pricing
  mrp?: number;
  minSellingPriceMinor?: number;
  minSellingPrice?: number;
  purchasePriceMinor: number;
  standardCostMinor?: number;
  sellingPriceMinor: number;
  marginPercent?: number;
  markupPercent?: number;

  // Tax — tax1Id/tax2Id are what this form edits; taxProfileId is the
  // composite record the rest of the app (quotations, orders, POS, delivery
  // challans) still reads directly, kept in sync server-side.
  tax1Id?: string | null;
  tax2Id?: string | null;
  taxProfileId?: string | null;
  categoryId?: string | null;
  brandId?: string | null;

  // Additional Details — reorder stock
  minQty?: number;
  maxQty?: number;

  // Stock
  isStockManaged: boolean;
  currentStock: number;
  openingStock?: number;
  reorderPoint?: number;
  safetyStock?: number;
  leadTimeDays?: number;
  trackBatch: boolean;
  trackSerial: boolean;
  trackExpiry?: boolean;
  valuationMethod?: string;
  taxInclusivePricing?: boolean;
  alternateUnits?: AlternateUnit[];
  openingStockEntries?: unknown[];
  priceHistory?: PriceHistoryEntry[];

  // Attributes
  weightKg?: number;
  volumeLiters?: number;

  // Service-type fields
  durationMinutes?: number;
  billableUnit?: string;
  requiredSkill?: string;
  assignedTeam?: string;
  timesheetLinked?: boolean;

  // GL mappings
  glInventoryAccount?: string;
  glPurchaseVariance?: string;
}
